import { useEffect, useState } from "react";
import { Banner, Button, Field, ProgressBar, Switch, TextInput, useCkToast } from "@NP-Webber/crm-kit/dist/next/index.js";
import { mailingApi, type MailingSettingsDto } from "../../api/mailingApi";
import { errorMessage, fmtNum } from "./mailingUi";

/** Platform (super_admin): an organization's monthly mailing quota, overage and suspension (ADR-0033). */
export function PlatformMailingPanel({ tenantId }: { tenantId: string }) {
  const toast = useCkToast();
  const [s, setS] = useState<MailingSettingsDto | null>(null);
  const [quota, setQuota] = useState("");
  const [allowOverage, setAllowOverage] = useState(false);
  const [suspended, setSuspended] = useState(false);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const apply = (x: MailingSettingsDto) => {
    setS(x);
    setQuota(x.customQuota ? String(x.monthlyQuota) : "");
    setAllowOverage(x.allowOverage);
    setSuspended(x.suspended);
    setReason(x.suspendedReason ?? "");
  };

  useEffect(() => {
    mailingApi.platform
      .get(tenantId)
      .then(apply)
      .catch((e) => setError(errorMessage(e, "לא הצלחנו לטעון את הגדרות הדיוור")));
  }, [tenantId]);

  const save = async () => {
    const q = quota.trim();
    if (q && (!/^\d+$/.test(q) || Number(q) > 10_000_000)) {
      toast.error("מכסה לא תקינה");
      return;
    }
    setBusy(true);
    try {
      apply(await mailingApi.platform.save(tenantId, { monthlyQuota: q ? Number(q) : null, allowOverage, suspended, suspendedReason: suspended ? reason : null }));
      toast.success("נשמר");
    } catch (e) {
      toast.error(errorMessage(e, "השמירה נכשלה"));
    } finally {
      setBusy(false);
    }
  };

  if (error) return <Banner tone="danger">{error}</Banner>;
  if (!s) return null;
  const used = Math.min(100, Math.round(((s.quota.used + s.quota.pending) / Math.max(1, s.quota.quota)) * 100));
  return (
    <div className="related-panel" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <ProgressBar value={used} tone={used >= 90 ? "danger" : "primary"} label={`החודש: ${fmtNum(s.quota.used)} נשלחו · ${fmtNum(s.quota.pending)} בתור · מכסה ${fmtNum(s.quota.quota)}`} />
      <Field label="מכסה חודשית" hint="ריק = ברירת המחדל של המערכת (MAILING_DEFAULT_MONTHLY_QUOTA)">
        <TextInput ltr inputMode="numeric" value={quota} onChange={(e) => setQuota(e.target.value)} placeholder={String(s.monthlyQuota)} />
      </Field>
      <Switch checked={allowOverage} onChange={setAllowOverage} label="מותר לחרוג מהמכסה" />
      <Switch checked={suspended} onChange={setSuspended} label="השליחה מושעית" hint="דיוורים פעילים נעצרים. כשמסירים השעיה אוטומטית — הם ממשיכים." />
      {suspended ? (
        <Field label="סיבת ההשעיה (מוצגת לארגון)">
          <TextInput value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} />
        </Field>
      ) : null}
      <div>
        <Button variant="primary" icon="check" onClick={() => void save()} disabled={busy}>
          {busy ? "שומר…" : "שמירה"}
        </Button>
      </div>
    </div>
  );
}
